import { TFunction } from 'i18next';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { LOGIN_ERROR_PAGE_PATH } from '../../nav/config';
import { useAppDependenciesContext } from '../../state';
import { getRawQueryParams, parseQueryParams, requireKeys } from '../../utils';
import { CodeExchangeParamsNotFoundError } from '../error/CodeExchangeParamsNotFoundError';
import { CodeExchangeQueryParams } from '../types';
import { CUSTOM_REDIRECT_URL_QUERY_PARAM_KEYS, getCustomRedirectUrlQueryParams } from './redirectUrl';

const REQUIRED_AUTH0_QUERY_PARAMS: Set<keyof CodeExchangeQueryParams> = new Set(['code', 'state']);

function validateAuth0RedirectParamsExist(t: TFunction, queryString: string) {
  const queryParams = parseQueryParams<Partial<CodeExchangeQueryParams>>(queryString) ?? {};
  const [_, missingKeys] = requireKeys(queryParams, REQUIRED_AUTH0_QUERY_PARAMS);
  if (missingKeys) {
    throw new CodeExchangeParamsNotFoundError({
      missingKeys,
      title: t('telehealth-core:errorsCore.invalidPage'),
      subtitle: t('telehealth-core:errorsCore.youMayHaveAccidentallyEndedUpHere'),
    });
  }
}

export function useLoginRedirectCallbackPageViewModel() {
  const t = useTranslate();
  const queryString = useLocation().search;
  const { useNavigate, useAuth0, logger } = useAppDependenciesContext();
  const { error: authError } = useAuth0();
  const navigate = useNavigate();

  useEffect(function validateAuth0RedirectParamsOnMount() {
    validateAuth0RedirectParamsExist(t, queryString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function handleAuth0Error() {
      if (authError) {
        logger.error('Auth0 code exchange failed', getCustomRedirectUrlQueryParams(queryString), authError);
        navigate(
          {
            pathname: LOGIN_ERROR_PAGE_PATH,
            search: getRawQueryParams(queryString, CUSTOM_REDIRECT_URL_QUERY_PARAM_KEYS),
          },
          {
            replace: true,
            state: {
              authError,
            },
          },
        );
      }
    },
    [authError, navigate, queryString, logger],
  );
}
